import { css, cx } from '@lastbrand/web-ui-styled-system/css'
import { usePathname } from 'next/navigation'
import { useTranslation } from 'next-i18next'
import React, { useCallback, useMemo } from 'react'

import { useStickyFiltersBar } from '@src/components/blocks/collection-filters-ds/use-sticky-filters-bar'
import { ALL_SUBCOLLECTION_FILTER_ID } from '@src/pages/[...slug]/components/collection/collection.constants'
import { SubCollection } from '@src/pages/[...slug]/components/collection/domains/Collection'
import { SubCollectionCardListProps } from '@src/pages/[...slug]/components/collection/molecules/subCollectionCardList/sub-collection-card-list.types'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import { useOptimisticPaint } from '@src/quince-site-common/src/contexts/responsive-interactions-context/responsive-interactions-context'
import ImageDomain from '@src/quince-site-common/src/domains/Image'
import { useResettableOptimisticValue } from '@src/quince-site-common/src/hooks/use-optimistic-selection'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { getDisplayKey } from '@src/utils/i18n.utils'

import DesktopSubCollectionCarousel from './sub-collection-carousel-desktop'
import MobileSubCollectionCarousel from './sub-collection-carousel-mobile'

// Pixel mirror of pillsStuckClass's `paddingTop: 'space-24'` (see useStickyFiltersBar).
const STUCK_TOP_PADDING_PX = 24

// Mobile pills stack under the nav on scroll-up, between the nav and the stuck filters
// bar (STRFRNT-15961): fixed at the viewport top, pushed down by translateY(navBottom)
// like the filters bar, hidden while the nav is hidden so scroll-down shows filters only.
// Chips sit at the bottom of the bar per the Figma spec — the larger gap goes above.
// No own divider: the stuck filters bar pinned right below draws the stack's single
// bottom border (on prod the pills' border exists but the filters bar overlaps it).
const pillsStuckClass = css({
  position: 'fixed',
  top: '0',
  left: '0',
  right: '0',
  zIndex: 44,
  paddingTop: 'space-24',
  paddingBottom: 'space-8',
  backgroundColor: 'white',
})

// The bar owns the pills' bottom spacing (not the carousel), so the stuck state can
// swap it for its own bottom padding; the hook's spacer covers margin + height.
const pillsBarClass = css({
  marginBottom: 'space-16',
})

export default function SubCollectionCardListDS({
  subCollections = [],
  onCardClick,
  isSubCollectionSelected,
  collectionExploreEverythingImage,
}: SubCollectionCardListProps) {
  const optimisticPaint = useOptimisticPaint()
  const path = usePathname()
  const { t } = useTranslation('common')
  const isNewLocalisedFlow = useI18nPreviewContext()

  // Inert at desktop widths: the mobile branch is display:none there, so every
  // measurement is 0 and the published stack height stays 0.
  const { sentinelRef, barRef, isStuck, isNavHidden, translateY, spacerHeight } = useStickyFiltersBar({
    stuckTopPaddingPx: STUCK_TOP_PADDING_PX,
    stack: 'contribute',
    // Scrolling down, the pills scroll off naturally and only pin once fully gone —
    // flipping to the (invisible-while-nav-hidden) fixed bar mid-viewport would blink
    // them out and leave the spacer's white gap where they still were.
    stickOnFullExit: true,
  })

  const allSubCollections = useMemo(() => {
    if (collectionExploreEverythingImage && subCollections.length > 1) {
      const imageDesktop = new ImageDomain({
        id: collectionExploreEverythingImage.id,
        title: collectionExploreEverythingImage.title,
        description: collectionExploreEverythingImage.description,
        url: collectionExploreEverythingImage.file.url,
        fileName: collectionExploreEverythingImage.file.fileName,
        height: collectionExploreEverythingImage.file.details.image.height,
        width: collectionExploreEverythingImage.file.details.image.width,
        loading: 'eager',
        displayModelInfo: false,
      })

      const allOption = new SubCollection({
        id: ALL_SUBCOLLECTION_FILTER_ID,
        title: t('all_subcollection'),
        imageDesktop,
        slug: path.split('/').slice(0, -1).join('/') || path,
      })

      return [allOption, ...subCollections]
    }

    return subCollections
  }, [subCollections, collectionExploreEverythingImage, path, t])

  const selectedSubcollectionIds = useMemo(() => {
    if (!isSubCollectionSelected) return []
    return allSubCollections
      .slice(1)
      .filter(sc => isSubCollectionSelected(sc))
      .map(sc => String(sc.id))
  }, [allSubCollections, isSubCollectionSelected])

  const hasAnySubcollectionSelected = useCallback(() => {
    return selectedSubcollectionIds.length > 0
  }, [selectedSubcollectionIds])

  const committedSelectionKey = isSubCollectionSelected
    ? selectedSubcollectionIds.join('|') || ALL_SUBCOLLECTION_FILTER_ID
    : path

  // Optimistic paint: single-value id overlay (string = a card id is optimistically selected;
  // ALL_SUBCOLLECTION_FILTER_ID = "none selected" paint; undefined = no override, use committed state).
  const { optimisticValue: optimisticId, setOptimisticValueAndDeferCommit } = useResettableOptimisticValue<string>(
    committedSelectionKey,
    optimisticPaint,
  )

  // Committed selected state (no optimistic overlay).
  const committedIsSelected = useCallback(
    (subCollection: SubCollection) => {
      if (!isSubCollectionSelected) return path === subCollection.slug

      if (subCollection.id === ALL_SUBCOLLECTION_FILTER_ID) {
        return !hasAnySubcollectionSelected()
      }

      return isSubCollectionSelected(subCollection)
    },
    [isSubCollectionSelected, path, hasAnySubcollectionSelected],
  )

  // Effective selected state: optimistic overlay layered over committed state.
  // When `optimisticPaint` is off or no optimistic pick is live, falls back to committed state
  // (byte-for-byte identical to pre-optimization behavior).
  const isSelected = useCallback(
    (subCollection: SubCollection): boolean => {
      if (!optimisticPaint || optimisticId === undefined) {
        return committedIsSelected(subCollection)
      }
      // Overlay is live: paint the desired state.
      // optimisticId === ALL_SUBCOLLECTION_FILTER_ID means "none selected" (All highlighted, others off).
      if (subCollection.id === ALL_SUBCOLLECTION_FILTER_ID) {
        return optimisticId === ALL_SUBCOLLECTION_FILTER_ID
      }
      return String(subCollection.id) === optimisticId
    },
    [optimisticPaint, optimisticId, committedIsSelected],
  )

  const trackChipClick = useCallback((subCollection: SubCollection) => {
    trackEvent(TELEMETRY_EVENT_NAMES.SUBCOLLECTION_CARD_CLICK, {
      slug: subCollection.slug,
      title: subCollection.title,
    })
  }, [])

  const trackAndClick = useCallback(
    (subCollection: SubCollection) => {
      trackChipClick(subCollection)
      onCardClick(subCollection)
    },
    [onCardClick, trackChipClick],
  )

  const handleChipClick = useCallback(
    (subCollection: SubCollection) => {
      if (subCollection.id === ALL_SUBCOLLECTION_FILTER_ID) {
        if (hasAnySubcollectionSelected()) {
          if (optimisticPaint) {
            // Paint "none selected" (All chip highlighted, all others off) immediately.
            setOptimisticValueAndDeferCommit(ALL_SUBCOLLECTION_FILTER_ID, () => {
              trackAndClick(subCollection)
            })
          } else {
            trackAndClick(subCollection)
          }
        } else {
          trackChipClick(subCollection)
        }
        return
      }

      if (optimisticPaint) {
        // Determine desired paint: if this chip is already the effective selected chip, paint
        // "none selected" (toggle-off to All). Otherwise paint this chip as selected.
        const isCurrentlyEffectivelySelected = isSelected(subCollection)
        const desiredId = isCurrentlyEffectivelySelected ? ALL_SUBCOLLECTION_FILTER_ID : String(subCollection.id)
        setOptimisticValueAndDeferCommit(desiredId, () => {
          trackAndClick(subCollection)
        })
      } else {
        trackAndClick(subCollection)
      }
    },
    [
      hasAnySubcollectionSelected,
      optimisticPaint,
      isSelected,
      setOptimisticValueAndDeferCommit,
      trackAndClick,
      trackChipClick,
    ],
  )

  const getLabel = useCallback(
    (subCollection: SubCollection) => {
      const displayLabel = getDisplayKey(subCollection, 'displayTitle', subCollection.title, isNewLocalisedFlow)

      return displayLabel ?? subCollection.title ?? ''
    },
    [isNewLocalisedFlow],
  )

  return (
    <section
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: 'space-16',
        width: 'full',
        marginBottom: { base: '0', lg: 'space-24' },
        paddingX: { base: '0', lg: 'space-48' },
      })}
    >
      <div
        className={css({
          display: { base: 'none', lg: 'block' },
          width: 'full',
          maxWidth: '1504px',
          marginX: 'auto',
        })}
      >
        <DesktopSubCollectionCarousel
          subCollections={allSubCollections}
          isSelected={isSelected}
          onChipClick={handleChipClick}
          getLabel={getLabel}
        />
      </div>

      <div className={css({ display: { base: 'block', lg: 'none' } })}>
        <div ref={sentinelRef} aria-hidden="true" />
        {isStuck && <div style={{ height: spacerHeight }} aria-hidden="true" />}
        <div
          ref={barRef}
          className={cx(pillsBarClass, isStuck ? pillsStuckClass : undefined)}
          style={
            isStuck
              ? { transform: `translateY(${translateY}px)`, visibility: isNavHidden ? 'hidden' : undefined }
              : undefined
          }
          data-testid="subcollection-pills-bar"
        >
          <MobileSubCollectionCarousel
            subCollections={allSubCollections}
            isSelected={isSelected}
            onChipClick={handleChipClick}
            getLabel={getLabel}
          />
        </div>
      </div>
    </section>
  )
}
